class ModalButton extends HTMLElement {
  connectedCallback() {
    this.clickable =
      this.querySelector("button") || this.querySelector("a") || this;
    this.targetModal = this.getAttribute("data-modal-target");
    this.handleClick = this.handleClick.bind(this);
    this.clickable.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.clickable?.removeEventListener("click", this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.targetModal) {
      const modal = document.querySelector(
        `side-drawer[data-modal-id="${this.targetModal}"]`
      );
      if (modal) {
        modal.open();
      }
    }
  }
}

class CustomModal extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector(".modal-overlay");
    this.container = this.querySelector(".modal-container");
    this.closeBtn = this.querySelector(".modal-close");
    this.content = this.querySelector(".modal-content");
    this.isOpen = false;
    this.desktopDirection =
      this.getAttribute("data-direction-desktop") || "left";
    this.mobileDirection = this.getAttribute("data-direction-mobile") || "left";
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.closeBtn?.addEventListener("click", this.close);
    this.overlay?.addEventListener("click", this.handleOverlayClick);
    window.addEventListener("resize", this.handleResize);
    this.setupStyles();
  }

  disconnectedCallback() {
    this.closeBtn?.removeEventListener("click", this.close);
    this.overlay?.removeEventListener("click", this.handleOverlayClick);
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  handleResize() {
    if (!this.isOpen) {
      this.setInitialTransform();
      const isDesktop = window.matchMedia("(min-width: 768px)").matches;
      const isFadeOnly =
        this.classList.contains("fade-only") ||
        this.hasAttribute("data-fade-only");
      if (this.container) {
        if (isDesktop && isFadeOnly) {
          this.container.style.transition =
            "opacity 0.3s ease, transform 0.3s ease";
        } else {
          this.container.style.transition = "transform 0.3s ease-in-out";
        }
      }
    }
  }

  setupStyles() {
    if (!this.container) return;
    const isFadeOnly =
      this.classList.contains("fade-only") ||
      this.hasAttribute("data-fade-only");
    this.setInitialTransform();
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    if (isDesktop && isFadeOnly) {
      this.container.style.transition =
        "opacity 0.3s ease, transform 0.3s ease";
    } else {
      this.container.style.transition = "transform 0.3s ease-in-out";
    }
  }

  setInitialTransform() {
    if (!this.container) return;
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    const isFadeOnly =
      this.classList.contains("fade-only") ||
      this.hasAttribute("data-fade-only");
    if (isDesktop && isFadeOnly) {
      this.container.style.transform = "translateY(-50%) scale(0.95)";
      this.container.style.opacity = "0";
    } else if (isDesktop) {
      const direction = this.desktopDirection;
      const translateValue =
        direction === "right"
          ? "translateY(-50%) translateX(100%)"
          : "translateY(-50%) translateX(-100%)";
      this.container.style.transform = translateValue;
    } else {
      this.container.style.transform = "translateY(100%)";
    }
  }

  getCurrentDirection() {
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    return isDesktop ? this.desktopDirection : this.mobileDirection;
  }

  open() {
    if (this.isOpen) return;
    this.scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.classList.add("side-drawer-open");
    this.setInitialTransform();
    this.classList.add("open");
    this.overlay?.classList.add("open");
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    const isFadeOnly =
      this.classList.contains("fade-only") ||
      this.hasAttribute("data-fade-only");
    if (isDesktop && isFadeOnly) {
      this.container?.offsetHeight;
      if (this.container) {
        this.container.style.opacity = "1";
        this.container.style.transform = "translateY(-50%) scale(1)";
      }
    } else if (isDesktop) {
      this.container?.offsetHeight;
      if (this.container) {
        this.container.style.transform = "translateY(-50%) translateX(0)";
      }
    } else {
      this.container?.offsetHeight;
      if (this.container) {
        this.container.style.transform = "translateY(0)";
      }
    }
    this.isOpen = true;
    document.addEventListener("keydown", this.handleKeyDown);
    const modalId = this.getAttribute("data-modal-id");
    window.dispatchEvent(
      new CustomEvent("modal:open", {
        detail: { modalId },
      })
    );
  }

  close() {
    if (!this.isOpen) return;
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    const isFadeOnly =
      this.classList.contains("fade-only") ||
      this.hasAttribute("data-fade-only");
    if (isDesktop && isFadeOnly) {
      if (this.container) {
        this.container.style.opacity = "0";
        this.container.style.transform = "translateY(-50%) scale(0.95)";
      }
    } else if (isDesktop) {
      if (this.container) {
        const direction = this.desktopDirection;
        const translateValue =
          direction === "right"
            ? "translateY(-50%) translateX(110%)"
            : "translateY(-50%) translateX(-100%)";
        this.container.style.transform = translateValue;
      }
    } else {
      if (this.container) {
        this.container.style.transform = "translateY(100%)";
      }
    }
    this.overlay?.classList.remove("open");
    setTimeout(() => {
      this.classList.remove("open");
      const scrollY = parseInt(document.body.style.top || "0") * -1;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.classList.remove("side-drawer-open");
      window.scrollTo(0, scrollY);
    }, 300);
    this.isOpen = false;
    document.removeEventListener("keydown", this.handleKeyDown);
    const modalId = this.getAttribute("data-modal-id");
    window.dispatchEvent(
      new CustomEvent("modal:close", {
        detail: { modalId },
      })
    );
  }

  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close();
    }
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }
}

customElements.define("side-drawer-button", ModalButton);
customElements.define("side-drawer", CustomModal);